/**
 * YearwiseAnalysis – Year-over-year comparison of Class 10 / 12 results.
 *
 * Uses GET /api/result/year-comparison-filter which returns multi-year data in one call.
 *
 * Level views:
 *   Overall  – KVS national aggregate (region = 'KVS OVERALL') across categories
 *   RO-wise  – Each Regional Office compared across selected years
 *   KV-wise  – Each school compared across selected years (loaded on demand)
 *
 * Categories (for Overall & RO-wise):
 *   SEX, SECTOR, URBAN_RURAL, PMSHRI, SCST, CWSN, STREAM (Class 12 only)
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, ChevronLeft, ArrowUpDown, ArrowUp, ArrowDown, Building2, School, LayoutGrid, Search, Download, Layers, GitCompare, BookOpen, ChevronRight } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import {
  useGetResultYearValueDropdownQuery,
  useGetYearComparisonQuery,
} from '../services/api';
import type { YearComparisonRecord, YearComparisonYearData } from '../services/api';
import type { GradeTab } from '../components/classification/types';
import { GRADE_TABS } from '../components/classification/constants';
import SectionLabel from '../components/classification/SectionLabel';
import KVSLoader from '../../../components/loaders/KVSLoader';

// ── Types ─────────────────────────────────────────────────────────────────────

type LevelView = 'overall' | 'ro' | 'kv';
type MetricKey = 'passPercentage' | 'performanceIndex';

// ── Constants ─────────────────────────────────────────────────────────────────

const KVS_OVERALL = 'KVS OVERALL';

const YEAR_COLORS = ['#F36B21', '#2563EB', '#16A34A', '#9333EA', '#DC2626', '#0891B2', '#D97706'];

const SERIES_COLORS: Record<string, string> = {
  Boys: '#2563EB',
  Girls: '#EC4899',
  General: '#16A34A',
  OBC: '#F59E0B',
  SC: '#6366F1',
  ST: '#EF4444',
  Urban: '#0891B2',
  Rural: '#65A30D',
  Civil: '#1D4ED8',
  Defence: '#7C3AED',
  Project: '#D97706',
  IHL: '#059669',
  'PM SHRI': '#F36B21',
  'Non-PM SHRI': '#6B7280',
  CWSN: '#8B5CF6',
  'Non-CWSN': '#94A3B8',
  Science: '#2563EB',
  Commerce: '#D97706',
  'Arts/Humanities': '#16A34A',
  Vocational: '#9333EA',
  Appeared: '#2563EB',
  Passed: '#16A34A',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function yrLabel(y: number): string {
  return `${y - 1}-${String(y).slice(-2)}`;
}

function pct(n: unknown): number {
  const v = Number(n);
  return Number.isFinite(v) ? +v.toFixed(2) : 0;
}

function aggregateMetric(
  records: YearComparisonRecord[],
  metricKey: MetricKey,
): number {
  if (!records.length) return 0;
  const totalAppeared = records.reduce((s, r) => s + r.totalAppeared, 0);
  if (totalAppeared === 0) return 0;
  if (metricKey === 'passPercentage') {
    const totalPassed = records.reduce((s, r) => s + r.totalPassed, 0);
    return +(totalPassed / totalAppeared * 100).toFixed(2);
  }
  return +(records.reduce((s, r) => s + r.performanceIndex * r.totalAppeared, 0) / totalAppeared).toFixed(2);
}

function getOverallRecords(yearsData: YearComparisonYearData[] | null | undefined): Array<{ year: number; records: YearComparisonRecord[] }> {
  if (!yearsData) return [];
  return yearsData
    .map(yd => ({ year: yd.year, records: yd.data.filter(r => r.region === KVS_OVERALL) }))
    .filter(({ records }) => records.length > 0);
}

function getDynamicPercentageFloor(values: number[]): number {
  if (!values.length) return 0;
  const minValue = Math.min(...values);
  return Math.max(0, Math.floor(minValue));
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface GroupedBarChartProps {
  title: string;
  subtitle?: string;
  data: Array<Record<string, string | number>>;
  xKey: string;
  seriesKeys: string[];
  unit?: string;
  height?: number;
  numberFormat?: boolean;
}

function GroupedBarChart({ title, subtitle, data, xKey, seriesKeys, unit = '%', height = 300, numberFormat = false }: GroupedBarChartProps) {
  const fmt = (v: number) => numberFormat ? v.toLocaleString('en-IN') : `${v}${unit}`;
  const isPercentageMetric = !numberFormat && unit === '%';
  const isPerformanceIndexMetric = !numberFormat && unit === '';
  const metricValues = (isPercentageMetric || isPerformanceIndexMetric)
    ? data.flatMap(row => seriesKeys.map(key => Number(row[key])).filter(Number.isFinite))
    : [];
  const axisDomain: [number, number | 'auto'] = (isPercentageMetric || isPerformanceIndexMetric)
    ? [getDynamicPercentageFloor(metricValues), isPercentageMetric ? 100 : 'auto']
    : [0, 'auto'];

  if (!data?.length || !seriesKeys.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">{title}</h3>
        <div className="h-36 flex items-center justify-center text-gray-400 text-sm">No data available</div>
      </div>
    );
  }

  const minWidth = Math.max(360, data.length * seriesKeys.length * 36 + 80);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="overflow-x-auto">
        <div style={{ minWidth }}>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data} margin={{ top: 20, right: 16, left: 0, bottom: 8 }} barGap={2} barCategoryGap="28%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis dataKey={xKey} tick={{ fontSize: 13, fill: '#374151' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis
                tick={{ fontSize: 12, fill: '#374151' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmt}
                width={numberFormat ? 72 : 52}
                domain={axisDomain}
                allowDataOverflow={isPercentageMetric || isPerformanceIndexMetric}
              />
              <Tooltip
                formatter={(val: any, name: any) => [fmt(typeof val === 'number' ? val : 0), name ?? '']}
                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
              />
              <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
              {seriesKeys.map((key, idx) => (
                <Bar key={key} dataKey={key} fill={SERIES_COLORS[key] ?? YEAR_COLORS[idx % YEAR_COLORS.length]} radius={[3, 3, 0, 0]} maxBarSize={40}>
                  <LabelList dataKey={key} position="top" style={{ fontSize: 13, fill: '#374151' }} formatter={(v: any) => fmt(typeof v === 'number' ? v : 0)} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// RO-wise horizontal bar chart (x = metric, y = RO name, series = years)
interface RoChartProps {
  data: Array<Record<string, string | number>>;
  years: string[];
  unit: string;
}

function RoComparisonChart({ data, years, unit }: RoChartProps) {
  if (!data.length || !years.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 h-40 flex items-center justify-center text-gray-400 text-sm">
        No data available
      </div>
    );
  }

  const fmt = (v: number) => `${v}${unit}`;
  const minHeight = Math.max(400, data.length * 36);
  const isPercentageMetric = unit === '%';
  const isPerformanceIndexMetric = unit === '';
  const metricValues = (isPercentageMetric || isPerformanceIndexMetric)
    ? data.flatMap(row => years.map(year => Number(row[year])).filter(Number.isFinite))
    : [];
  const axisDomain: [number, number | 'auto'] = (isPercentageMetric || isPerformanceIndexMetric)
    ? [getDynamicPercentageFloor(metricValues), isPercentageMetric ? 100 : 'auto']
    : [0, 'auto'];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="overflow-x-auto">
        <ResponsiveContainer width="100%" height={minHeight}>
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 8, right: 48, left: 8, bottom: 8 }}
            barGap={2}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={fmt}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
              domain={axisDomain}
              allowDataOverflow={isPercentageMetric || isPerformanceIndexMetric}
            />
            <YAxis type="category" dataKey="ro" width={120} tick={{ fontSize: 13, fill: '#374151' }} axisLine={false} tickLine={false} />
            <Tooltip
              formatter={(val: any, name: any) => [fmt(typeof val === 'number' ? val : 0), name ?? '']}
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 13, paddingBottom: 4 }} />
            {years.map((yr, i) => (
              <Bar key={yr} dataKey={yr} fill={YEAR_COLORS[i % YEAR_COLORS.length]} radius={[0, 3, 3, 0]} maxBarSize={20}>
                <LabelList dataKey={yr} position="right" style={{ fontSize: 13, fill: '#374151' }} formatter={(v: any) => fmt(typeof v === 'number' ? v : 0)} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Summary stat card
function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-sm text-gray-600 font-medium mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      {sub && <p className="text-sm text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function YearwiseAnalysis() {
  const navigate = useNavigate();

  const [activeClass, setActiveClass] = useState<GradeTab>('class10');
  const [levelView, setLevelView] = useState<LevelView>('overall');
  const [metric, setMetric] = useState<'pass' | 'pi'>('pass');
  const [startYear, setStartYear] = useState<number | null>(null);
  const [endYear, setEndYear] = useState<number | null>(null);
  const [kvSearch, setKvSearch] = useState('');
  const [kvTopBottomMode, setKvTopBottomMode] = useState<'none' | 'top' | 'bottom'>('none');
  const [kvTopBottomN, setKvTopBottomN] = useState('10');
  const [kvMetricMin, setKvMetricMin] = useState('');
  const [kvMetricMax, setKvMetricMax] = useState('');

  const grade: 10 | 12 = activeClass === 'class10' ? 10 : 12;
  const metricKey: MetricKey = metric === 'pass' ? 'passPercentage' : 'performanceIndex';
  const metricLabel = metric === 'pass' ? 'Pass %' : 'Performance Index (PI)';
  const unit = metric === 'pass' ? '%' : '';

  const { data: yearDropdownData, isLoading: yearsLoading } = useGetResultYearValueDropdownQuery({ grade });

  const yearOptions = useMemo(() => {
    const nums = (yearDropdownData || []).map(Number).filter(Number.isFinite);
    return nums.length > 0 ? [...nums].sort((a, b) => a - b) : [];
  }, [yearDropdownData]);

  // Effective year range (default to last 3 years)
  const effectiveStart = startYear ?? yearOptions[Math.max(0, yearOptions.length - 3)] ?? null;
  const effectiveEnd = endYear ?? yearOptions[yearOptions.length - 1] ?? null;

  const queryReady = !!effectiveStart && !!effectiveEnd && effectiveStart <= effectiveEnd;
  const baseArgs = { grade, level: 'RO' as const, startingYear: effectiveStart ?? 0, endYear: effectiveEnd ?? 0 };

  // ── Category queries (RO level) ──
  const { data: sexData, isLoading: sexLoading } = useGetYearComparisonQuery(
    { ...baseArgs, category: 'SEX' }, { skip: !queryReady }
  );
  const { data: sectorData, isLoading: sectorLoading } = useGetYearComparisonQuery(
    { ...baseArgs, category: 'SECTOR' }, { skip: !queryReady }
  );
  const { data: urbanRuralData, isLoading: urbanLoading } = useGetYearComparisonQuery(
    { ...baseArgs, category: 'URBAN_RURAL' }, { skip: !queryReady }
  );
  const { data: pmshriData, isLoading: pmshriLoading } = useGetYearComparisonQuery(
    { ...baseArgs, category: 'PMSHRI' }, { skip: !queryReady }
  );
  const { data: scstData, isLoading: scstLoading } = useGetYearComparisonQuery(
    { ...baseArgs, category: 'SCST' }, { skip: !queryReady }
  );
  const { data: cwsnData, isLoading: cwsnLoading } = useGetYearComparisonQuery(
    { ...baseArgs, category: 'CWSN' }, { skip: !queryReady }
  );
  const { data: streamData, isLoading: streamLoading } = useGetYearComparisonQuery(
    { ...baseArgs, category: 'STREAM' }, { skip: !queryReady || grade !== 12 }
  );

  // KV-level (only when KV tab active to avoid large payload until needed)
  const { data: kvData, isLoading: kvLoading } = useGetYearComparisonQuery(
    { grade, level: 'KV', category: 'SEX', startingYear: effectiveStart ?? 0, endYear: effectiveEnd ?? 0 },
    { skip: !queryReady || levelView !== 'kv' }
  );

  // ── Overall (KVS aggregate) data memos ────────────────────────────────────

  // All-years summary table for Overall tab
  const allYearsSummary = useMemo(() => {
    const rows = getOverallRecords(sexData).map(({ year, records }) => {
      const totalAppeared = records.reduce((s, r) => s + r.totalAppeared, 0);
      const totalPassed   = records.reduce((s, r) => s + r.totalPassed, 0);
      const passPercentage = totalAppeared > 0 ? +(totalPassed / totalAppeared * 100).toFixed(2) : 0;
      const pi = +(records.reduce((s, r) => s + r.performanceIndex * r.totalAppeared, 0) / (totalAppeared || 1)).toFixed(2);
      return { year, totalAppeared, totalPassed, passPercentage, pi };
    });
    // attach deviation from previous year
    return rows.map((row, i) => ({
      ...row,
      passDev: i === 0 ? null : +(row.passPercentage - rows[i - 1].passPercentage).toFixed(2),
      piDev:   i === 0 ? null : +(row.pi - rows[i - 1].pi).toFixed(2),
    }));
  }, [sexData]);

  // Latest year for backward-compat
  const latestOverall = useMemo(() => {
    const overallByYear = getOverallRecords(sexData);
    if (!overallByYear.length) return null;
    const latest = overallByYear[overallByYear.length - 1];
    const totalAppeared = latest.records.reduce((s, r) => s + r.totalAppeared, 0);
    const totalPassed = latest.records.reduce((s, r) => s + r.totalPassed, 0);
    const passPercentage = totalAppeared > 0 ? +(totalPassed / totalAppeared * 100).toFixed(2) : 0;
    const pi = +(latest.records.reduce((s, r) => s + r.performanceIndex * r.totalAppeared, 0) / (totalAppeared || 1)).toFixed(2);
    return { year: latest.year, totalAppeared, totalPassed, passPercentage, pi };
  }, [sexData]);

  // Overall pass% / PI trend
  const overallTrend = useMemo(() => {
    return getOverallRecords(sexData).map(({ year, records }) => ({
      year: yrLabel(year),
      value: aggregateMetric(records, metricKey),
    }));
  }, [sexData, metricKey]);

  // Appeared vs Passed trend
  const appearedPassedTrend = useMemo(() => {
    return getOverallRecords(sexData).map(({ year, records }) => ({
      year: yrLabel(year),
      Appeared: records.reduce((s, r) => s + r.totalAppeared, 0),
      Passed: records.reduce((s, r) => s + r.totalPassed, 0),
    }));
  }, [sexData]);

  // ── Category-grouped (cross-year) chart builders ─────────────────────────
  // These flip the axes: X = category label, series bars = years

  // Gender — Boys vs Girls, one group per gender, bars = years
  const genderCrossYear = useMemo(() => {
    const overallByYear = getOverallRecords(sexData);
    const years = overallByYear.map(({ year }) => yrLabel(year));
    const boys: Record<string, string | number> = { category: 'Boys' };
    const girls: Record<string, string | number> = { category: 'Girls' };
    overallByYear.forEach(({ year, records }) => {
      const lbl = yrLabel(year);
      boys[lbl] = pct(records.find(r => /^m/i.test(r.sex ?? ''))?.[metricKey]);
      girls[lbl] = pct(records.find(r => /^f/i.test(r.sex ?? ''))?.[metricKey]);
    });
    return { data: [boys, girls], years };
  }, [sexData, metricKey]);

  // Social Category — General / OBC / SC / ST
  const categoryCrossYear = useMemo(() => {
    const overallByYear = getOverallRecords(scstData);
    const years = overallByYear.map(({ year }) => yrLabel(year));
    const cats = ['GENERAL', 'OBC', 'SC', 'ST'];
    const catLabels: Record<string, string> = { GENERAL: 'General', OBC: 'OBC', SC: 'SC', ST: 'ST' };
    const rows = cats.map(cat => {
      const row: Record<string, string | number> = { category: catLabels[cat] };
      overallByYear.forEach(({ year, records }) => {
        const lbl = yrLabel(year);
        row[lbl] = pct(records.find(r => r.socialCategory?.toUpperCase() === cat)?.[metricKey]);
      });
      return row;
    });
    return { data: rows, years };
  }, [scstData, metricKey]);

  // Location — Urban vs Rural
  const locationCrossYear = useMemo(() => {
    const overallByYear = getOverallRecords(urbanRuralData);
    const years = overallByYear.map(({ year }) => yrLabel(year));
    const rows = ['Urban', 'Rural'].map(loc => {
      const row: Record<string, string | number> = { category: loc };
      overallByYear.forEach(({ year, records }) => {
        const lbl = yrLabel(year);
        row[lbl] = pct(records.find(r => r.urbanRural?.toUpperCase() === loc.toUpperCase())?.[metricKey]);
      });
      return row;
    });
    return { data: rows, years };
  }, [urbanRuralData, metricKey]);

  // Sector — Civil / Defence / Project / IHL
  const sectorCrossYear = useMemo(() => {
    const overallByYear = getOverallRecords(sectorData);
    const years = overallByYear.map(({ year }) => yrLabel(year));
    const sectors = ['Civil', 'Defence', 'Project', 'IHL'];
    const rows = sectors.map(sec => {
      const row: Record<string, string | number> = { category: sec };
      overallByYear.forEach(({ year, records }) => {
        const lbl = yrLabel(year);
        row[lbl] = pct(records.find(r => r.sector?.toUpperCase() === sec.toUpperCase())?.[metricKey]);
      });
      return row;
    });
    return { data: rows, years };
  }, [sectorData, metricKey]);

  // PM SHRI
  const pmshriCrossYear = useMemo(() => {
    const overallByYear = getOverallRecords(pmshriData);
    const years = overallByYear.map(({ year }) => yrLabel(year));
    const rows = [
      { key: 'PM SHRI', test: (r: YearComparisonRecord) => r.isPmshri === true },
      { key: 'Non-PM SHRI', test: (r: YearComparisonRecord) => r.isPmshri === false },
    ].map(({ key, test }) => {
      const row: Record<string, string | number> = { category: key };
      overallByYear.forEach(({ year, records }) => {
        const lbl = yrLabel(year);
        row[lbl] = pct(records.find(test)?.[metricKey]);
      });
      return row;
    });
    return { data: rows, years };
  }, [pmshriData, metricKey]);

  // CWSN — only show Non-CWSN row if data actually has Non-CWSN records
  const cwsnCrossYear = useMemo(() => {
    const overallByYear = getOverallRecords(cwsnData);
    const years = overallByYear.map(({ year }) => yrLabel(year));
    const hasCwsn = overallByYear.some(({ records }) => records.some(r => r.isCwsn === true));
    const hasNonCwsn = overallByYear.some(({ records }) => records.some(r => r.isCwsn === false));
    const candidates = [
      { key: 'CWSN', show: hasCwsn, test: (r: YearComparisonRecord) => r.isCwsn === true },
      { key: 'Non-CWSN', show: hasNonCwsn, test: (r: YearComparisonRecord) => r.isCwsn === false },
    ].filter(c => c.show);
    const rows = candidates.map(({ key, test }) => {
      const row: Record<string, string | number> = { category: key };
      overallByYear.forEach(({ year, records }) => {
        const lbl = yrLabel(year);
        row[lbl] = pct(records.find(test)?.[metricKey]);
      });
      return row;
    });
    return { data: rows, years };
  }, [cwsnData, metricKey]);

  // Stream (Class 12)
  const streamCrossYear = useMemo(() => {
    if (grade !== 12 || !streamData) return { data: [], years: [] };
    const overallByYear = getOverallRecords(streamData);
    const years = overallByYear.map(({ year }) => yrLabel(year));
    const find = (records: YearComparisonRecord[], kw: string) =>
      records.find(r => (r.stream ?? r.streamLabel ?? '').toUpperCase().includes(kw.toUpperCase()));
    const streams = [
      { key: 'Science', kw: 'SCIENCE' },
      { key: 'Commerce', kw: 'COMMERCE' },
      { key: 'Arts/Humanities', kw: 'HUMANITIES', kw2: 'ARTS' },
      { key: 'Vocational', kw: 'VOCATIONAL' },
    ];
    const rows = streams.map(({ key, kw, kw2 }) => {
      const row: Record<string, string | number> = { category: key };
      overallByYear.forEach(({ year, records }) => {
        const lbl = yrLabel(year);
        row[lbl] = pct((find(records, kw) ?? (kw2 ? find(records, kw2) : undefined))?.[metricKey]);
      });
      return row;
    });
    return { data: rows, years };
  }, [streamData, metricKey, grade]);

  // Legacy (still used for overall trend chart)
  const genderTrend = useMemo(() => {
    return getOverallRecords(sexData).map(({ year, records }) => ({
      year: yrLabel(year),
      Boys: pct(records.find(r => /^m/i.test(r.sex ?? ''))?.[metricKey]),
      Girls: pct(records.find(r => /^f/i.test(r.sex ?? ''))?.[metricKey]),
    }));
  }, [sexData, metricKey]);

  // ── RO-wise chart data ─────────────────────────────────────────────────────

  const { roChartData, roYears } = useMemo(() => {
    if (!sexData) return { roChartData: [], roYears: [] };
    const years = sexData.map(yd => yrLabel(yd.year));
    const roNames = new Set<string>();
    sexData.forEach(yd => yd.data.filter(r => r.region && r.region !== KVS_OVERALL).forEach(r => roNames.add(r.region!)));
    const roChartData = Array.from(roNames)
      .map(ro => {
        const row: Record<string, string | number> = { ro };
        sexData.forEach(yd => {
          const recs = yd.data.filter(r => r.region === ro);
          row[yrLabel(yd.year)] = aggregateMetric(recs, metricKey);
        });
        return row;
      })
      .sort((a, b) => {
        const lastYear = years[years.length - 1];
        return Number(b[lastYear] ?? 0) - Number(a[lastYear] ?? 0);
      });
    return { roChartData, roYears: years };
  }, [sexData, metricKey]);

  // ── KV-wise table data ─────────────────────────────────────────────────────

  const { kvTableData, kvYears } = useMemo(() => {
    if (!kvData) return { kvTableData: [], kvYears: [] };
    const years = kvData.map(yd => yrLabel(yd.year));

    // Group by kvCode
    const kvMap = new Map<string, { schoolName: string; region: string; data: Record<string, number> }>();
    kvData.forEach(yd => {
      const yearLabel = yrLabel(yd.year);
      // Group records per KV per year (aggregate M+F)
      const kvGroups = new Map<string, YearComparisonRecord[]>();
      yd.data.forEach(r => {
        if (!r.kvCode) return;
        const key = String(r.kvCode);
        if (!kvGroups.has(key)) kvGroups.set(key, []);
        kvGroups.get(key)!.push(r);
      });
      kvGroups.forEach((records, key) => {
        if (!kvMap.has(key)) {
          kvMap.set(key, { schoolName: records[0].schoolName ?? key, region: records[0].region ?? '', data: {} });
        }
        kvMap.get(key)!.data[yearLabel] = aggregateMetric(records, metricKey);
      });
    });

    const rows = Array.from(kvMap.entries()).map(([kv, info]) => ({ kv, ...info }));
    const lastYr = years[years.length - 1];
    rows.sort((a, b) => (b.data[lastYr] ?? 0) - (a.data[lastYr] ?? 0));
    return { kvTableData: rows, kvYears: years };
  }, [kvData, metricKey]);

  // KV range filter: 'all' means at least one year in range satisfies, specific year = exact year
  const [kvRangeYearFilter, setKvRangeYearFilter] = useState<'all' | string>('all');

  const filteredKvData = useMemo(() => {
    const q = kvSearch.toLowerCase().trim();
    const searched = q
      ? kvTableData.filter(r =>
          r.schoolName.toLowerCase().includes(q) ||
          r.region.toLowerCase().includes(q) ||
          r.kv.toLowerCase().includes(q)
        )
      : kvTableData;
    if (kvMetricMin === '' && kvMetricMax === '') return searched;
    const min = kvMetricMin !== '' ? parseFloat(kvMetricMin) : -Infinity;
    const max = kvMetricMax !== '' ? parseFloat(kvMetricMax) : Infinity;
    return searched.filter(r => {
      if (kvRangeYearFilter === 'all') {
        // Pass if ALL years satisfy the range (cascade together)
        return kvYears.every(y => {
          const val = r.data[y] ?? 0;
          return val >= min && val <= max;
        });
      }
      // Specific year
      const val = r.data[kvRangeYearFilter] ?? 0;
      return val >= min && val <= max;
    });
  }, [kvTableData, kvSearch, kvYears, kvMetricMin, kvMetricMax, kvRangeYearFilter]);

  const kvN = Math.max(1, parseInt(kvTopBottomN, 10) || 10);
  const kvDisplayRows =
    kvTopBottomMode === 'top' ? filteredKvData.slice(0, kvN)
    : kvTopBottomMode === 'bottom' ? filteredKvData.slice(-kvN)
    : filteredKvData;

  function downloadKvCSV() {
    const headers = ['#', 'School Name', 'Region', ...kvYears];
    const rows = kvDisplayRows.map(function (r, i) {
      return [
        i + 1,
        `"${r.schoolName.replace(/"/g, '""')}"`,
        `"${r.region.replace(/"/g, '""')}"`,
        ...kvYears.map(y => r.data[y] !== undefined ? r.data[y] : ''),
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kv-yearwise-performance.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Loading states ─────────────────────────────────────────────────────────

  const overallLoading = sexLoading || sectorLoading || urbanLoading || pmshriLoading || scstLoading || cwsnLoading || (grade === 12 && streamLoading);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4">

        {/* Title row */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/result/classification')}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#F36B21] font-medium transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Classification
            </button>
            <div className="h-5 w-px bg-gray-300" />
            <div>
              <h1 className="text-2xl font-bold text-[#F36B21] flex items-center gap-2">
                <TrendingUp className="w-7 h-7" />
                Year-wise Analysis
              </h1>
              <p className="text-gray-500 text-sm mt-0.5">Multi-year trend comparison for CBSE Board results</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2.5 flex-shrink-0">
            <button
              onClick={function () { navigate('/result/classification'); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
            >
              <Layers className="w-4 h-4" />
              Classification Analysis
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={function () { navigate('/result/comparison'); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
            >
              <GitCompare className="w-4 h-4" />
              Class 10 & 12 Comparison
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={function () { navigate('/result/subject-analysis'); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Subject-wise Analysis
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Filter panel card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">

          {/* Row 1: Class + Year range + Metric */}
          <div className="px-6 py-3 flex flex-wrap items-center gap-4">

            {/* Class toggle */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">Class</span>
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                {GRADE_TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveClass(tab.id)}
                    className={
                      'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ' +
                      (activeClass === tab.id ? 'bg-white text-[#F36B21] shadow-sm' : 'text-gray-500 hover:text-gray-700')
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Separator */}
            <div className="h-8 w-px bg-gray-200 flex-shrink-0 hidden sm:block" />

            {/* Year range */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">Years</span>
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5">
                <span className="text-sm text-gray-500 font-medium">From</span>
                <select
                  value={effectiveStart ?? ''}
                  onChange={e => setStartYear(Number(e.target.value))}
                  className="text-sm font-semibold text-gray-700 bg-transparent border-0 outline-none cursor-pointer"
                  disabled={yearsLoading || yearOptions.length === 0}
                >
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{yrLabel(y)}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-400">→</span>
                <select
                  value={effectiveEnd ?? ''}
                  onChange={e => setEndYear(Number(e.target.value))}
                  className="text-sm font-semibold text-gray-700 bg-transparent border-0 outline-none cursor-pointer"
                  disabled={yearsLoading || yearOptions.length === 0}
                >
                  {yearOptions.filter(y => !effectiveStart || y >= effectiveStart).map(y => (
                    <option key={y} value={y}>{yrLabel(y)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Separator */}
            <div className="h-8 w-px bg-gray-200 flex-shrink-0 hidden sm:block" />

            {/* Metric toggle */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">Metric</span>
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                {(['pass', 'pi'] as const).map(m => (
                  <button
                    key={m}
                    onClick={() => setMetric(m)}
                    className={
                      'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-1.5 ' +
                      (metric === m ? 'bg-white text-[#F36B21] shadow-sm' : 'text-gray-500 hover:text-gray-700')
                    }
                  >
                    <ArrowUpDown className="w-3 h-3" />
                    {m === 'pass' ? 'Pass %' : 'PI'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 2: Level tabs */}
          <div className="px-6 py-3 flex items-center gap-2">
            <span className="text-sm font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap w-14 flex-shrink-0">View</span>
            <div className="flex gap-1">
              {([
                { id: 'overall' as LevelView, label: 'KVS Overall', icon: <LayoutGrid className="w-3.5 h-3.5" /> },
                { id: 'ro' as LevelView, label: 'RO-wise', icon: <Building2 className="w-3.5 h-3.5" /> },
                { id: 'kv' as LevelView, label: 'KV-wise', icon: <School className="w-3.5 h-3.5" /> },
              ]).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setLevelView(tab.id)}
                  className={
                    'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ' +
                    (levelView === tab.id
                      ? 'bg-orange-50 text-[#F36B21] border border-orange-200'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')
                  }
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Loading / No years state ────────────────────────────────────── */}
      {(yearsLoading || !queryReady) && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <KVSLoader size={72} color="#F36B21" />
            <p className="text-sm text-gray-400 mt-4">Loading year data…</p>
          </div>
        </div>
      )}

      {/* ── OVERALL VIEW ───────────────────────────────────────────────── */}
      {queryReady && levelView === 'overall' && (
        <>
          {overallLoading && (
            <div className="flex justify-center py-16">
              <KVSLoader size={60} color="#F36B21" />
            </div>
          )}

          {!overallLoading && (
            <div className="space-y-8">

              {/* All-years summary table */}
              {allYearsSummary.length > 0 && (
                <div>
                  <SectionLabel>KVS Overall — Year-wise Summary</SectionLabel>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left text-sm font-semibold text-gray-600 px-5 py-3">Year</th>
                            <th className="text-right text-sm font-semibold text-gray-600 px-5 py-3">Total Appeared</th>
                            <th className="text-right text-sm font-semibold text-gray-600 px-5 py-3">Total Passed</th>
                            <th className="text-right text-sm font-semibold text-gray-700 px-5 py-3 bg-green-50/50">Pass %</th>
                            <th className="text-right text-sm font-semibold text-gray-600 px-5 py-3">↕ Pass% Change</th>
                            <th className="text-right text-sm font-semibold text-gray-700 px-5 py-3 bg-blue-50/50">Perf. Index (PI)</th>
                            <th className="text-right text-sm font-semibold text-gray-600 px-5 py-3">↕ PI Change</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {[...allYearsSummary].reverse().map((row) => {
                            const isLatest = row.year === allYearsSummary[allYearsSummary.length - 1].year;
                            return (
                              <tr key={row.year} className={`transition-colors ${isLatest ? 'bg-orange-50/30' : 'hover:bg-gray-50'}`}>
                                <td className="px-5 py-3 font-semibold text-gray-800">
                                  {yrLabel(row.year)}
                                  {isLatest && <span className="ml-2 text-xs bg-[#F36B21] text-white px-1.5 py-0.5 rounded-full font-bold">Latest</span>}
                                </td>
                                <td className="px-5 py-3 text-right text-gray-700 font-medium">{row.totalAppeared.toLocaleString('en-IN')}</td>
                                <td className="px-5 py-3 text-right text-gray-700 font-medium">{row.totalPassed.toLocaleString('en-IN')}</td>
                                <td className="px-5 py-3 text-right font-bold text-green-700 bg-green-50/20">{row.passPercentage}%</td>
                                <td className="px-5 py-3 text-right">
                                  {row.passDev === null ? (
                                    <span className="text-gray-400">—</span>
                                  ) : (
                                    <span className={`font-semibold flex items-center justify-end gap-1 ${
                                      row.passDev > 0 ? 'text-green-600' : row.passDev < 0 ? 'text-red-500' : 'text-gray-500'
                                    }`}>
                                      {row.passDev > 0 ? '▲' : row.passDev < 0 ? '▼' : '='}
                                      {Math.abs(row.passDev).toFixed(2)}%
                                    </span>
                                  )}
                                </td>
                                <td className="px-5 py-3 text-right font-bold text-blue-700 bg-blue-50/20">{row.pi}</td>
                                <td className="px-5 py-3 text-right">
                                  {row.piDev === null ? (
                                    <span className="text-gray-400">—</span>
                                  ) : (
                                    <span className={`font-semibold flex items-center justify-end gap-1 ${
                                      row.piDev > 0 ? 'text-green-600' : row.piDev < 0 ? 'text-red-500' : 'text-gray-500'
                                    }`}>
                                      {row.piDev > 0 ? '▲' : row.piDev < 0 ? '▼' : '='}
                                      {Math.abs(row.piDev).toFixed(2)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Overall trend */}
              <div className="space-y-4">
                <SectionLabel>Overall Trend — {metricLabel}</SectionLabel>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <GroupedBarChart
                    title={`${metricLabel} Year-over-Year`}
                    data={overallTrend.map(d => ({ year: d.year, [metricLabel]: d.value }))}
                    xKey="year"
                    seriesKeys={[metricLabel]}
                    unit={unit}
                    height={260}
                  />
                  <GroupedBarChart
                    title="Appeared vs Passed Trend"
                    data={appearedPassedTrend}
                    xKey="year"
                    seriesKeys={['Appeared', 'Passed']}
                    unit=""
                    numberFormat
                    height={260}
                  />
                </div>
              </div>

              {/* Gender — category on X, bars = years */}
              <div className="space-y-3">
                <SectionLabel>Gender-wise — {metricLabel}</SectionLabel>
                <GroupedBarChart
                  title={`Boys vs Girls — ${metricLabel}`}
                  subtitle="Each group shows the same gender across selected years for cross-year comparison"
                  data={genderCrossYear.data}
                  xKey="category"
                  seriesKeys={genderCrossYear.years}
                  unit={unit}
                />
              </div>

              {/* Social Category — category on X, bars = years */}
              <div className="space-y-3">
                <SectionLabel>Social Category-wise — {metricLabel}</SectionLabel>
                <GroupedBarChart
                  title={`General / OBC / SC / ST — ${metricLabel}`}
                  subtitle="Each group shows the same category across selected years for cross-year comparison"
                  data={categoryCrossYear.data}
                  xKey="category"
                  seriesKeys={categoryCrossYear.years}
                  unit={unit}
                />
              </div>

              {/* Location — category on X, bars = years */}
              <div className="space-y-3">
                <SectionLabel>Location-wise — {metricLabel}</SectionLabel>
                <GroupedBarChart
                  title={`Urban vs Rural — ${metricLabel}`}
                  subtitle="Each group shows the same location type across selected years for cross-year comparison"
                  data={locationCrossYear.data}
                  xKey="category"
                  seriesKeys={locationCrossYear.years}
                  unit={unit}
                />
              </div>

              {/* Sector — category on X, bars = years */}
              <div className="space-y-3">
                <SectionLabel>Sector-wise — {metricLabel}</SectionLabel>
                <GroupedBarChart
                  title={`Civil / Defence / Project / IHL — ${metricLabel}`}
                  subtitle="Each group shows the same sector across selected years for cross-year comparison"
                  data={sectorCrossYear.data}
                  xKey="category"
                  seriesKeys={sectorCrossYear.years}
                  unit={unit}
                />
              </div>

              {/* PM SHRI — category on X, bars = years */}
              <div className="space-y-3">
                <SectionLabel>PM SHRI Schools — {metricLabel}</SectionLabel>
                <GroupedBarChart
                  title={`PM SHRI vs Non-PM SHRI — ${metricLabel}`}
                  subtitle="Each group shows the same PM SHRI type across selected years for cross-year comparison"
                  data={pmshriCrossYear.data}
                  xKey="category"
                  seriesKeys={pmshriCrossYear.years}
                  unit={unit}
                />
              </div>

              {/* CWSN — category on X, bars = years; Non-CWSN hidden when no data */}
              <div className="space-y-3">
                <SectionLabel>CWSN — {metricLabel}</SectionLabel>
                <GroupedBarChart
                  title={
                    cwsnCrossYear.data.length === 1
                      ? `CWSN — ${metricLabel}`
                      : `CWSN vs Non-CWSN — ${metricLabel}`
                  }
                  subtitle={
                    cwsnCrossYear.data.length === 1
                      ? 'Only CWSN data available (Non-CWSN not reported)'
                      : 'Each group shows the same CWSN type across selected years for cross-year comparison'
                  }
                  data={cwsnCrossYear.data}
                  xKey="category"
                  seriesKeys={cwsnCrossYear.years}
                  unit={unit}
                />
              </div>

              {/* Stream (Class 12 only) */}
              {grade === 12 && (
                <div className="space-y-3">
                  <SectionLabel>Stream-wise — {metricLabel} (Class 12)</SectionLabel>
                  <GroupedBarChart
                    title={`Science / Commerce / Arts / Vocational — ${metricLabel}`}
                    subtitle="Each group shows the same stream across selected years for cross-year comparison"
                    data={streamCrossYear.data}
                    xKey="category"
                    seriesKeys={streamCrossYear.years}
                    unit={unit}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── RO-WISE VIEW ───────────────────────────────────────────────── */}
      {queryReady && levelView === 'ro' && (
        <div className="space-y-4">
          <SectionLabel>Regional Office Comparison — {metricLabel}</SectionLabel>

          {sexLoading ? (
            <div className="flex justify-center py-16"><KVSLoader size={60} color="#F36B21" /></div>
          ) : (
            <>
              <p className="text-sm text-gray-400">
                Each RO's {metricLabel} across selected years. Values are aggregated across genders.
              </p>
              <RoComparisonChart data={roChartData} years={roYears} unit={unit} />
            </>
          )}
        </div>
      )}

      {/* ── KV-WISE VIEW ───────────────────────────────────────────────── */}
      {queryReady && levelView === 'kv' && (
        <div className="space-y-4">
          <SectionLabel>School-wise Comparison — {metricLabel}</SectionLabel>

          {kvLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <KVSLoader size={60} color="#F36B21" />
              <p className="text-sm text-gray-400">Loading school data…</p>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 max-w-xs">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search school, region or KV code…"
                  value={kvSearch}
                  onChange={e => setKvSearch(e.target.value)}
                  className="text-sm text-gray-700 bg-transparent outline-none w-full placeholder-gray-400"
                />
              </div>

              <p className="text-sm text-gray-400">
                Showing {kvDisplayRows.length} of {filteredKvData.length} schools · Sorted by {kvYears[kvYears.length - 1] ?? 'latest'} {metricLabel}
              </p>

              {/* Filter bar */}
              <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 text-sm">
                <div className="flex rounded-lg overflow-hidden border border-gray-200">
                  {(['none', 'top', 'bottom'] as const).map(mode => (
                    <button key={mode} onClick={() => setKvTopBottomMode(mode)} className={'px-3 py-1.5 text-sm font-medium transition-colors ' + (kvTopBottomMode === mode ? 'bg-[#F36B21] text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
                      {mode === 'none' ? 'All' : mode === 'top' ? 'Top' : 'Bottom'}
                    </button>
                  ))}
                </div>
                <input type="number" min="1" value={kvTopBottomN} onChange={e => setKvTopBottomN(e.target.value)} disabled={kvTopBottomMode === 'none'} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-40" placeholder="N" />

                {/* Range filter with year selector */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-gray-400 text-sm">{metricLabel} range</span>
                  <select
                    value={kvRangeYearFilter}
                    onChange={e => setKvRangeYearFilter(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 cursor-pointer"
                  >
                    <option value="all">All Years (cascade)</option>
                    {kvYears.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <input type="number" placeholder="Min" value={kvMetricMin} onChange={e => setKvMetricMin(e.target.value)} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white" />
                  <span className="text-gray-300">–</span>
                  <input type="number" placeholder="Max" value={kvMetricMax} onChange={e => setKvMetricMax(e.target.value)} className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white" />
                </div>

                <span className="ml-auto text-sm text-gray-400">{kvDisplayRows.length} rows</span>
                <button onClick={downloadKvCSV} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F36B21] text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors">
                  <Download className="w-3.5 h-3.5" />CSV
                </button>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left text-sm font-semibold text-gray-600 px-4 py-3 w-8">#</th>
                        <th className="text-left text-sm font-semibold text-gray-600 px-4 py-3">KV Code</th>
                        <th className="text-left text-sm font-semibold text-gray-600 px-4 py-3">School Name</th>
                        <th className="text-left text-sm font-semibold text-gray-600 px-4 py-3">Region</th>
                        {kvYears.map((y, i) => (
                          <th
                            key={y}
                            className="text-right text-sm font-semibold px-4 py-3 whitespace-nowrap text-gray-700"
                            style={{ backgroundColor: `${YEAR_COLORS[i % YEAR_COLORS.length]}1A` }}
                          >
                            {y}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {kvDisplayRows.map((row, idx) => (
                        <tr key={row.kv} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2.5 text-gray-500 text-sm font-medium">{idx + 1}</td>
                          <td className="px-4 py-2.5 text-gray-600 text-sm font-mono">{row.kv}</td>
                          <td className="px-4 py-2.5 text-gray-800 font-semibold text-sm">{row.schoolName}</td>
                          <td className="px-4 py-2.5 text-gray-600 text-sm">{row.region}</td>
                          {kvYears.map((y, i) => (
                            <td
                              key={y}
                              className="px-4 py-2.5 text-right font-semibold text-gray-700 text-sm"
                              style={{ backgroundColor: `${YEAR_COLORS[i % YEAR_COLORS.length]}14` }}
                            >
                              {row.data[y] !== undefined ? `${row.data[y]}${unit}` : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {kvDisplayRows.length === 0 && (
                        <tr>
                          <td colSpan={3 + kvYears.length} className="text-center text-gray-400 text-sm py-12">
                            No schools match your search
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

